import { DateTime } from 'luxon';

import { formatDateWithPreset } from './dates';

/**
 * Formatting utilities for admin interfaces
 */

// Re-export currency formatting from currency.ts to avoid duplication
export { formatCurrencyAmount as formatCurrency } from './currency';

/**
 * Derives avatar initials from an entity or user name.
 *
 * Splits on whitespace, keeps only words that start with an alphanumeric
 * character (so punctuation-only tokens like "(", "&" or "-" are skipped),
 * and returns the uppercased first letter of the first two such words —
 * always at most 2 characters.
 */
export const getInitials = (name: string): string => {
  return name
    .split(/\s+/)
    .filter((word) => /^[a-zA-Z0-9]/.test(word))
    .slice(0, 2)
    .map((word) => word.charAt(0).toUpperCase())
    .join('');
};

/**
 * Formats numbers with thousands separators
 */
export const formatNumber = (
  value: number | string,
  locale = 'en-US',
  options: Intl.NumberFormatOptions = {}
): string => {
  const numericValue = typeof value === 'string' ? parseFloat(value) : value;

  if (Number.isNaN(numericValue)) return '—';

  return new Intl.NumberFormat(locale, options).format(numericValue);
};

/**
 * Formats percentage values
 */
export const formatPercentage = (
  value: number | string,
  decimals = 1,
  locale = 'en-US'
): string => {
  const numericValue = typeof value === 'string' ? parseFloat(value) : value;

  if (Number.isNaN(numericValue)) return '—';

  return new Intl.NumberFormat(locale, {
    style: 'percent',
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  }).format(numericValue / 100);
};

/**
 * Formats phone numbers for display
 */
export const formatPhoneDisplay = (phoneNumber: string): string => {
  // Remove all non-numeric characters
  const cleaned = phoneNumber.replace(/\D/g, '');

  // Format US phone numbers
  if (cleaned.length === 10) {
    return `(${cleaned.slice(0, 3)}) ${cleaned.slice(3, 6)}-${cleaned.slice(
      6
    )}`;
  }

  // Format international numbers (basic formatting)
  if (cleaned.length > 10) {
    return `+${cleaned.slice(0, cleaned.length - 10)} ${cleaned.slice(-10)}`;
  }

  return phoneNumber;
};

/**
 * Formats dates for admin interfaces
 */
// Re-export date formatting from dates.ts to avoid duplication
export { formatDateWithPreset as formatDate } from './dates';

/**
 * Formats date and time for admin interfaces
 */
export const formatDateTime = (
  date: Date | string | DateTime,
  timezone?: string
): string => {
  return formatDateWithPreset(date, 'DATETIME', timezone);
};

// Re-export relative time formatting from dates.ts to avoid duplication
export { getRelativeTime as formatRelativeTime } from './dates';

/**
 * Formats file sizes (bytes to human readable)
 */
export const formatFileSize = (bytes: number): string => {
  if (bytes === 0) return '0 Bytes';

  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));

  return `${parseFloat((bytes / k ** i).toFixed(2))} ${sizes[i]}`;
};

/**
 * Truncates text with ellipsis
 */
export const truncateText = (text: string, maxLength: number): string => {
  if (text.length <= maxLength) return text;
  return `${text.slice(0, maxLength - 3)}...`;
};

/**
 * Capitalizes first letter of each word
 */
export const toTitleCase = (text: string): string => {
  return text.replace(
    /\w\S*/g,
    (txt) => txt.charAt(0).toUpperCase() + txt.substr(1).toLowerCase()
  );
};

/**
 * Formats camelCase to readable text
 */
export const camelCaseToReadable = (text: string): string => {
  return text
    .replace(/([A-Z])/g, ' $1')
    .replace(/^./, (str) => str.toUpperCase())
    .trim();
};

/**
 * Formats status for display
 */
export const formatStatus = (status: string): string => {
  return status
    .split('_')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ');
};
